/** TWILIO_WHATSAPP_SENDER control: show the operator's effective WhatsApp number + sender status,
 *  and register a per-operator sender via the v2 Senders API (needs a CP WABA id). Registration is
 *  async (Meta review) — the status refreshes it.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem, TwilioWhatsappDetail } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { copyOnlyActions } from '../tenantLinks';

export function TwilioWhatsappControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const seed = item.detail as unknown as TwilioWhatsappDetail;
  const query = useQuery({
    queryKey: ['preflight', 'twilioWhatsappStatus', topId, item.id],
    queryFn: () => items.fetchTwilioWhatsappStatus(topId, item.id),
    enabled: canManage,
  });
  const status = query.data;
  const detail = status?.detail ?? seed;

  const register = useMutation({
    mutationFn: () => items.registerTwilioWhatsappSender(topId, item.id, {}),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show(`Sender registered (${res.status ?? 'CREATING'}). Meta review is pending.`, 'success');
        void query.refetch();
        onChanged();
      } else {
        const msg =
          res.reason === 'no_waba'
            ? 'No WhatsApp Business Account id configured on the server.'
            : typeof res.detail === 'string'
              ? res.detail
              : 'Could not register the WhatsApp sender.';
        snackbar.show(msg, 'error');
      }
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not register the WhatsApp sender.', 'error'),
  });

  if (status?.reason === 'not_configured' || detail?.configured === false) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">Twilio isn&apos;t configured on the server.</Alert>
      </Box>
    );
  }

  const registered = !!detail?.sender_sid;
  const online = (detail?.sender_status ?? '').toUpperCase() === 'ONLINE';
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2">
            WhatsApp number:{' '}
            <ValueActions actions={copyOnlyActions(detail?.whatsapp_number, 'Copy WhatsApp number')}>
              <strong>{detail?.whatsapp_number || '—'}</strong>
            </ValueActions>
          </Typography>
          {detail?.uses_shared_default && (
            <Chip size="small" variant="outlined" label="shared platform number" />
          )}
        </Stack>

        {registered ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {online ? (
              <CheckCircleIcon fontSize="small" color="success" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
            )}
            <Typography variant="body2">
              Sender{' '}
              <ValueActions actions={copyOnlyActions(detail?.sender_sid, 'Copy sender SID')}>
                {detail?.sender_sid}
              </ValueActions>{' '}
              — status <strong>{detail?.sender_status || 'unknown'}</strong>
            </Typography>
            {query.isFetching && <CircularProgress size={14} />}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No per-operator WhatsApp sender registered.
          </Typography>
        )}

        {!detail?.waba_configured && (
          <Alert severity="info">
            A per-operator sender needs a CP WhatsApp Business Account id on the server; without it,
            the shared platform WhatsApp number is used.
          </Alert>
        )}

        {canManage && (
          <Stack direction="row" spacing={1}>
            {detail?.waba_configured && (
              <Button
                size="small"
                variant="contained"
                onClick={() => register.mutate()}
                disabled={register.isPending}
                startIcon={register.isPending ? <CircularProgress size={14} /> : undefined}
              >
                {registered ? 'Re-register sender' : 'Register WhatsApp sender'}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
            >
              Refresh status
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
